import { ApiService } from '@core/api.service';
import { inject, Injectable } from '@angular/core';
import { WebSocketService } from '@core/web-socket.service';
import { MenuService } from '@services/common/menu.service';
import { StorageService } from '@services/common/storage.service';
import { Collection } from 'collection';
import { CollectionModel } from 'collection';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';

export class EmitEvent {
  name: string;
  value: any;

  constructor({ name, value }: { name: string; value: any }) {
    this.name = name;
    this.value = value;
  }
}

@Injectable({
  providedIn: 'root'
})
export class MarketplaceService {
  private subject$ = new Subject();
  private _ws = inject(WebSocketService, { optional: true });
  private _menuService = inject(MenuService);

  emit(event: EmitEvent) {
    this.subject$.next(event);
  }

  on(event, action: any): Subscription {
    return this.subject$
      .pipe(
        filter((e: EmitEvent) => e.name === event),
        map((e) => e.value)
      )
      .subscribe(action);
  }

  collectionExternalIntegration: CollectionModel;
  collectionIntegration: CollectionModel;

  integrationsEnabled: Map<string, boolean> = new Map();

  isOpenSidebar: boolean = true;
  private _isOpenSidebar: BehaviorSubject<boolean> = new BehaviorSubject(this.isOpenSidebar);
  get $isOpenSidebar(): Observable<any> {
    return this._isOpenSidebar.asObservable();
  }

  toggleSidebar(): void {
    this.isOpenSidebar = !this.isOpenSidebar;
    this._isOpenSidebar.next(this.isOpenSidebar);
  }

  hasIntegrationEnabled(integrationId: string): boolean {
    if (this.integrationsEnabled.has(integrationId)) {
      return this.integrationsEnabled.get(integrationId);
    }

    return false;
  }

  setIntegrations(integrations): void {
    integrations.forEach((item) => {
      this.integrationsEnabled.set(item.id, item.is_enable);
    });

    const integrationsEnabled = Array.from(this.integrationsEnabled);
    this._localStorage.setItem('integrations', integrationsEnabled);
  }

  setIntegration(integration): void {
    this.integrationsEnabled.set(integration.id, integration.is_enable);
  }

  initIntegrations(): void {
    const integrationsEnabled = this._localStorage.getItem('integrations');
    this.integrationsEnabled = new Map(integrationsEnabled);
  }

  // duplicate
  updateAvailableIntegration(integrationId: string, isEnabled: boolean) {
    const data = [...this._menuService.menu.values()];

    data
      .flatMap((group) => Array.from(group.items))
      .filter((item) => {
        return item[1].integrationId && item[1].integrationId === integrationId;
      })
      .forEach((item) => {
        this._menuService.setMenu(item[1].groupName, item[1].title, { show: isEnabled }).applyMenu();
      });
  }

  private onUpdateIntegration(): void {
    this._ws
      .on('inner_integration')
      .pipe(
        map((integration: any) => {
          this.setIntegration(integration);
          this.updateAvailableIntegration(integration.id, integration.is_enable);
          this.emit(new EmitEvent({ name: integration.id, value: integration.is_enable }));
        })
      )
      .subscribe();
  }

  constructor(
    private _localStorage: StorageService,
    private _api: ApiService
  ) {
    this.collectionExternalIntegration = new Collection({
      api: this._api.externalIntegrations
    });

    this.collectionExternalIntegration.loaded = true;
    this.collectionExternalIntegration.hardReloadAfter.updating = true;
    this.collectionExternalIntegration.loadByScrolling = false;

    this.collectionIntegration = new Collection({
      api: this._api.integrations
    });

    this.collectionIntegration.loaded = true;
    this.collectionIntegration.hardReloadAfter.updating = true;
    this.collectionIntegration.loadByScrolling = false;

    this.collectionIntegration.onLoad$.subscribe((data) => {
      const integrations = data.items;
      this.setIntegrations(integrations);
    });

    this.initIntegrations();
    this.onUpdateIntegration();
  }
}
